/*
 * (c) 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 *
 * This AWS Content is provided subject to the terms of the AWS Customer Agreement available at http://aws.amazon.com/agreement or other written agreement between Customer and either Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
 */

import React from "react";
import { Box, Button, Cards, ContentLayout, Header, Select, SpaceBetween } from "@cloudscape-design/components";
import { ApiError, get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import GameList, { GameData } from "./GameList";
import AssetSearch from "./AssetSearch";
import workbenchLogo from '../assets/workbench_logo_white.png';

interface ApplicationProps {
    signOut: any
}

interface AuthContextProps {
    auth: any
}

type ActivePage = "landing" | "streams" | "asset-search";

interface RegionOption {
    label: string;
    value: string;
    description: string;
}

interface ApplicationState {
    activePage: ActivePage;
    selectedRegion: string;
    regions: RegionOption[];
    games: GameData[];
    gamesLoading: boolean;
    gamesError: string;
}

// Friendly display names for known region codes. Regions are discovered
// dynamically from the /games response; any code not listed here falls back
// to displaying the raw region code.
const REGION_LABELS: { [code: string]: string } = {
    "us-west-2": "Oregon",
    "us-east-2": "Ohio",
    "eu-central-1": "Frankfurt",
    "ap-northeast-1": "Tokyo",
    "eu-west-1": "Ireland",
    "us-east-1": "N. Virginia",
};

class Application extends React.Component<ApplicationProps & AuthContextProps, ApplicationState> {
    constructor(props: ApplicationProps & AuthContextProps) {
        super(props);
        this.state = {
            activePage: "landing",
            selectedRegion: "",
            regions: [],
            games: [],
            gamesLoading: false,
            gamesError: "",
        };

        this.changeRegion = this.changeRegion.bind(this);
    }

    // Fetch the list of games from the DynamoDB-backed gamecast-api. This is the
    // single source of truth for both the game cards and the set of regions we
    // offer, so it lives here in Application and is shared with GameList.
    fetchGames = async (): Promise<GameData[]> => {
        try {
            const session = await fetchAuthSession();
            const restOperation = get({
                apiName: "gamecast-api",
                path: "/games",
                options: {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
                    }
                }
            });
            const { body } = await restOperation.response;
            const games = JSON.parse(await body.text()) as GameData[];
            this.setState({ games, gamesLoading: false, gamesError: "" });
            return games;
        } catch (error) {
            console.error('Error fetching games:', error);

            if (error instanceof ApiError && error.response) {
                const { statusCode, body } = error.response;
                console.error(`API Error: ${statusCode} - ${body}`);
            }

            this.setState({ gamesLoading: false, gamesError: 'Failed to fetch games' });
            return [];
        }
    };

    // Build the region option list from the games returned by the API. The set
    // of regions is the union of every entry's regionStreamMap keys, so any
    // region where a stream is offered is shown even if no machines are
    // currently free there.
    private deriveRegions(games: GameData[]): RegionOption[] {
        const codes = new Set<string>();
        games.forEach((g) => {
            if (g.regionStreamMap) {
                Object.keys(g.regionStreamMap).forEach((code) => codes.add(code));
            }
        });

        return Array.from(codes).map((code) => ({
            label: REGION_LABELS[code] ?? code,
            value: code,
            description: code,
        }));
    }

    // Entering the Streams page: fetch games, derive the available regions from
    // them, then run the latency check across only those regions.
    private loadStreams = async () => {
        const games = await this.fetchGames();
        const regions = this.deriveRegions(games);

        if (regions.length === 0) {
            this.setState({ regions: [], selectedRegion: "" });
            return;
        }

        // Populate the dropdown immediately (before pings resolve) and keep the
        // current selection if it's still available, otherwise default to the
        // first region until latency results arrive.
        this.setState((prev) => ({
            regions,
            selectedRegion: regions.some((r) => r.value === prev.selectedRegion)
                ? prev.selectedRegion
                : regions[0].value,
        }));

        this.startLatencyCheck(regions);
    };

    private startLatencyCheck(regions: RegionOption[]) {
        Promise.all(regions.map((e) => this.region_latency(e.label, e.value, e.description)))
            .then((results) => {
                if (results.length === 0) {
                    return;
                }
                const sorted = results.sort((a, b) => a.latency - b.latency);
                console.log("Measured Latencies, default selecting best option: ", sorted);
                this.setState({
                    selectedRegion: sorted[0].value,
                    regions: sorted,
                });
            });
    }

    async region_latency(label: string, value: string, description: string, times: number = 5): Promise<{
        label: string;
        value: string;
        description: string;
        latency: number;
    }> {
        const url = `https://cheetah-test-eu-west-3-02.s3.${value}.amazonaws.com/`;
        const fetchOptions = {
            method: "HEAD",
            mode: "no-cors" as const,
            referrerPolicy: "no-referrer" as const,
        };

        let minLatency = Infinity;
        for (let i = 0; i < times; i++) {
            const startTime = performance.now();
            await fetch(url, fetchOptions);
            const latency = performance.now() - startTime;
            minLatency = Math.min(minLatency, latency);
        }

        return {
            label,
            value,
            description: `${description} -> ${minLatency.toFixed(0)}ms`,
            latency: minLatency,
        };
    }

    changeRegion(event: any) {
        this.setState({ selectedRegion: event.detail.selectedOption?.value });
    }

    navigateTo = (page: ActivePage) => {
        if (page === "streams") {
            this.setState({ activePage: page, gamesLoading: true });
            this.loadStreams();
            return;
        }
        this.setState({ activePage: page });
    };

    renderHeader() {
        const { activePage } = this.state;
        const regionSelection = this.state.regions.find((list) => this.state.selectedRegion === list.value);

        return (
            <SpaceBetween size="xl">
                <div style={{ backgroundColor: "#232F3E", padding: "20px" }}>
                    <Header
                        variant="h1"
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                {activePage !== "landing" && (
                                    <Button onClick={() => this.navigateTo("landing")}>Back</Button>
                                )}
                                {activePage === "streams" && (
                                    <Select
                                        placeholder={"Regions"}
                                        selectedOption={regionSelection ?? null}
                                        options={this.state.regions}
                                        onChange={this.changeRegion}
                                    />
                                )}
                                <Button variant="primary" onClick={this.props.signOut}>Sign Out</Button>
                            </SpaceBetween>
                        }
                    >
                        <img
                            src={workbenchLogo}
                            alt="Workbench"
                            style={{
                                height: "48px",
                                width: "auto",
                                display: "inline-block",
                                verticalAlign: "middle",
                                padding: "5px",
                                borderRadius: "4px",
                            }}
                        />
                    </Header>
                </div>
            </SpaceBetween>
        );
    }

    renderLanding() {
        return (
            <Box padding={{ top: "xl" }}>
                <Cards
                    cardDefinition={{
                        header: (item) => item.title,
                        sections: [
                            { id: "description", content: (item) => item.description },
                            {
                                id: "action",
                                content: (item) => (
                                    <Button variant="primary" onClick={() => this.navigateTo(item.page)}>
                                        {item.buttonText}
                                    </Button>
                                ),
                            },
                        ],
                    }}
                    items={[
                        {
                            title: "Game Streams",
                            description: "Launch and manage Workbench streaming sessions. Connects to the nearest AWS region for optimal performance.",
                            buttonText: "Open Streams",
                            page: "streams" as ActivePage,
                        },
                        {
                            title: "Asset Search",
                            description: "Search for 3D assets by ASIN or keyword. View thumbnails and download images. Results reflect models available in the Workbench application.",
                            buttonText: "Open Asset Search",
                            page: "asset-search" as ActivePage,
                        },
                    ]}
                    header={<Header variant="h2">Welcome to Workbench</Header>}
                    cardsPerRow={[{ cards: 1 }, { minWidth: 400, cards: 2 }]}
                />
            </Box>
        );
    }

    renderContent() {
        switch (this.state.activePage) {
            case "streams":
                return (
                    <SpaceBetween direction="vertical" size="m">
                        <GameList
                            region={this.state.selectedRegion}
                            auth={this.props.auth}
                            games={this.state.games}
                            loading={this.state.gamesLoading}
                            loadError={this.state.gamesError}
                            onRefresh={this.fetchGames}
                        />
                    </SpaceBetween>
                );
            case "asset-search":
                return <AssetSearch />;
            default:
                return this.renderLanding();
        }
    }

    render() {
        return (
            <div style={{
                backgroundColor: "#0f1b2a",
                margin: "0px",
                padding: "0px",
            }}>
                <ContentLayout
                    disableOverlap
                    headerVariant="high-contrast"
                    header={this.renderHeader()}
                >
                    {this.renderContent()}
                </ContentLayout>
            </div>
        );
    }
}

export default Application;
